import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { toast } from 'sonner'
import { ChevronDown } from 'lucide-react'

import {
  localizedWorkflowStepTitle,
  WorkflowRun,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

import { populationsApi } from '@/lib/api'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import i18n from '@/i18n'
import {
  WorkflowGateRevision,
  WorkflowGateSubmission,
} from '@/lib/workflows/useWorkflowRun'

import {
  collectGateArtifacts,
  GateArtifactEntry,
  GateRevisionHandle,
  GateRevisionOutcome,
  GateRevisionTail,
  isGateEditable,
  parseGateQuestions,
  pendingGateDocksOnComposer,
  planGateRevision,
} from './ThreadRunPane/gateArtifacts'
import { twinTestGateMaxQuestions } from './ThreadRunPane/gateQuestionEdits'
import {
  GateEditState,
  RunGateCard,
  buildRunGateCreditChip,
  useGateEditState,
} from './ThreadRunPane/RunGateCard'
import { buildRunSpendLine, summarizeRunGateSpend } from './ThreadRunPane/runSpendLine'
import { stimulusRefsFromRun } from './stimulusRefsFromRun'

/**
 * The approval gate WHERE THE READER IS.
 *
 * Questions payloads (`/test`) still dock on the composer (`aboveSlot`): that
 * preview is the thing being approved and has nowhere else to live. Scan,
 * brief, and report gates already posted their bodies in the thread, so
 * spend + Continue sit there too — inline like creative Confirm, not as a
 * nested “Waiting on you” card, and not as a modal over the composer.
 *
 * The page mounts this component in exactly one of those two places. The run
 * pane keeps showing the same gate as a watch-only record
 * (`RunGateCard` with `actionable={false}`), so the decision exists exactly
 * once even though the gate is visible twice.
 *
 * Renders nothing at all unless a gate step is genuinely `awaiting_approval`:
 * a decided gate is history and belongs to the pane's step list, not to the
 * bottom of the thread.
 *
 * IT ALSO HOLDS THE STAGED BODIES. The edits map used to be private state in a
 * card that mounts twice, which meant two independent maps and no way for a
 * rewrite to reach the one that decides. It lives here now, in the mount that
 * decides, and is handed down; the pane's watch-only copy keeps its own, which
 * stays empty because it cannot edit. Nothing about the single approve payload
 * changed: there is still no per-field save callback anywhere on this surface.
 */

interface ThreadRunGateProps {
  run: WorkflowRun | null
  template: WorkflowTemplate | null
  /**
   * Audience billing tier for the QS credit chip. Prefer passing when the
   * page already knows it (same as ConfirmRunCard / HomeStage: audience
   * `.tier`). When omitted, the dock looks up the run's audience input.
   * Missing or empty tier → chip omitted (no invented rate).
   */
  audienceTier?: string | null
  onSubmitGate: (submission: WorkflowGateSubmission) => void
  /**
   * Ask the writer to redo the step this gate is showing (D1). Resolves once
   * the returned run has been adopted, false when the server refused.
   *
   * The gate does NOT write questions and never splices a model's prose into a
   * card: the step that has the question-design rules, the org overlays and
   * the output checker writes them, and this is the door to it.
   */
  onReviseStep?: (revision: WorkflowGateRevision) => Promise<boolean>
  onCancelRun: () => void
  /** Thread / answer language for the seeded gate title. */
  language?: string | null
  /**
   * The thread page's wiring point for chat-driven revision.
   *
   * Called with a handle whenever a typed message may revise this gate, and
   * with null the moment it may not. A page holding null sends the message to
   * the ordinary assistant turn, which is what every gate that is decided,
   * read-only or somebody else's produces.
   *
   * The page CAPTURES the handle when it routes a send and calls
   * `applyRevision` on that captured copy when the turn completes, even if the
   * gate published null in the meantime: a turn outlives the gate that started
   * it, and the late call is what produces the discard notice.
   */
  onRevisionHandleChange?: (handle: GateRevisionHandle | null) => void
  /**
   * Bumped by the page once per composer send while the gate docks there
   * (D6). Each increment collapses the dock once; a re-render with the same
   * value never re-collapses it, so a reviewer who expands it back mid-turn
   * is not fought by an unrelated re-render. A fresh dock (new
   * `pendingGate.stepId`) always mounts expanded regardless of this value.
   */
  collapseSignal?: number
  /**
   * True while the dock is tweening its own height between collapsed and
   * expanded, false once it settles. The tween changes the dock's border box
   * every frame, which the page's composer and scroller ResizeObservers read
   * as real layout movement; the page uses this to sit out its scroll
   * corrections for the duration, because a height WE are animating on
   * purpose is not content arriving late.
   */
  onDockTweenChange?: (active: boolean) => void
  className?: string
}

/**
 * The bodies as the reviewer has them right now: a hand edit wins over the
 * artifact it replaced. This is what travels to the intake turn as read-only
 * context, so the model can tell a change to a drafted question from a brand
 * new one, and it is the same text the writer's redo starts from.
 */
function stagedBodies(
  artifacts: Array<GateArtifactEntry>,
  editState: GateEditState,
): Array<string> {
  return artifacts.map(
    (artifact) => editState.edits[artifact.key] ?? artifact.body,
  )
}

/**
 * Audience id stamped on the run — same input the twin-test step resolves via
 * `audienceFrom: input.audience`. Template key when declared; else `audience`.
 */
function audienceIdFromRun(
  run: WorkflowRun,
  template: WorkflowTemplate | null,
): string | null {
  const key =
    template?.inputs.find((spec) => spec.type === 'audience')?.key ?? 'audience'
  const raw = run.inputs[key]
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return null
  }
  return raw.trim()
}

export function ThreadRunGate(props: ThreadRunGateProps) {
  const {
    run,
    template,
    audienceTier = null,
    onSubmitGate,
    onReviseStep,
    onCancelRun,
    language = null,
    onRevisionHandleChange,
    collapseSignal = 0,
    onDockTweenChange,
    className,
  } = props

  const { t } = useTranslation('app')

  /* The first pending gate, not every gate: a run holds at most one open
     question at a time, and a second pending row would mean the server let two
     steps wait at once, which it does not. */
  const pendingGate =
    run?.steps.find(
      (step) => step.stepKind === 'gate' && step.state === 'awaiting_approval',
    ) ?? null

  const artifacts =
    run && pendingGate ? collectGateArtifacts(run, pendingGate, template) : []
  const isEditable = pendingGate ? isGateEditable(pendingGate, template) : false

  /* QS / HomeStage pattern: price from the selected audience's `.tier`. Prop
     wins when the page already has it; otherwise fetch the run's audience and
     read `.tier` — never invent a rate when tier is unknown. */
  const audienceId =
    run && pendingGate ? audienceIdFromRun(run, template) : null
  const stimulusRefs =
    run && pendingGate ? stimulusRefsFromRun(run, template) : []
  /* Declared on the template's own `quick_study` step rather than a
     hand-kept frontend mirror (D5): a later template with a different cap
     needs no change here. */
  const maxQuestions = twinTestGateMaxQuestions(template)
  const { data: gateAudience } = useSWR(
    audienceId && !audienceTier
      ? ['thread-run-gate-audience', audienceId]
      : null,
    () => populationsApi.get(audienceId!),
  )
  const resolvedTier = useMemo(() => {
    const fromProp =
      typeof audienceTier === 'string' && audienceTier.trim().length > 0
        ? audienceTier.trim()
        : null
    if (fromProp) {
      return fromProp
    }
    const fromAudience = gateAudience?.tier
    return fromAudience ? String(fromAudience) : null
  }, [audienceTier, gateAudience?.tier])

  /* Who may revise by chat: owner + template `editable`. Width does not
     matter — questions stay editable in the dock, and prose rewrites land
     in the same staged map even though those bodies are not replayed. */
  const canRevise = Boolean(pendingGate && run?.viewerOwnsRun && isEditable)

  const editState = useGateEditState(pendingGate?.stepId ?? null)
  const [notice, setNotice] = useState<string | null>(null)

  /* Composer-placement dock only (D6): one visible line, no Approve / Cancel /
     preview, until the reviewer expands it back. A fresh dock always mounts
     expanded, reset below whenever the pending step changes. */
  const [isCollapsed, setIsCollapsed] = useState(false)

  /* The dock's outer box, and the height it had the instant before a collapse
     or expand was asked for. The two states are different subtrees, so the
     browser would swap them in one frame and the composer under them would
     jump; instead the box's height is tweened from the captured value to the
     new one (Web Animations API, skipped where it is missing or where the
     reader prefers reduced motion), while the new content fades in. */
  const dockRef = useRef<HTMLDivElement | null>(null)
  const heightBeforeToggleRef = useRef<number | null>(null)
  /* Held in a ref for the same reason the revision handle below is: an inline
     arrow from the page would otherwise re-run the tween effect on every
     render and restart the animation mid-flight. */
  const publishDockTween = useRef(onDockTweenChange)
  useEffect(() => {
    publishDockTween.current = onDockTweenChange
  })
  const toggleCollapsed = useCallback((next: boolean) => {
    heightBeforeToggleRef.current =
      dockRef.current?.getBoundingClientRect().height ?? null
    setIsCollapsed(next)
  }, [])
  useLayoutEffect(() => {
    const element = dockRef.current
    const from = heightBeforeToggleRef.current
    heightBeforeToggleRef.current = null
    if (!element || from === null || typeof element.animate !== 'function') {
      return
    }
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }
    const to = element.getBoundingClientRect().height
    if (from === to) {
      return
    }
    const duration = 260
    element.style.overflow = 'hidden'
    /* Announced BEFORE the first frame: the page must already be sitting out
       its scroll corrections by the time the first resize observation of this
       tween reaches it. */
    publishDockTween.current?.(true)
    const animation = element.animate(
      [{ height: `${from}px` }, { height: `${to}px` }],
      { duration, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    )
    const release = () => {
      element.style.overflow = ''
      publishDockTween.current?.(false)
    }
    /* `finished` settles on finish AND on cancel (it rejects). A background
       tab may hold that settlement until it is shown again, so a timer lifts
       the clip regardless: nothing below the dock may stay clipped. */
    animation.finished.then(release, release)
    const fallback = window.setTimeout(release, duration + 50)
    return () => {
      window.clearTimeout(fallback)
      animation.cancel()
      release()
    }
  }, [isCollapsed])

  /* `collapseSignal` is a counter, not a boolean: the page bumps it once per
     composer send while docked, and only a CHANGE from the last value seen
     collapses the dock. A re-render carrying the same number (unrelated state
     changing elsewhere on the page) must never re-collapse a dock the reviewer
     just expanded back open. */
  const lastCollapseSignalRef = useRef(collapseSignal)
  useEffect(() => {
    if (collapseSignal !== lastCollapseSignalRef.current) {
      lastCollapseSignalRef.current = collapseSignal
      toggleCollapsed(true)
    }
  }, [collapseSignal, toggleCollapsed])

  /* Priced off the STAGED bodies, not the run's draft: a question the reviewer
     removed in the dock is not spent by this click, so the sentence and the
     chip follow the edits the approve payload will carry. */
  const creditChip =
    run && pendingGate && resolvedTier
      ? buildRunGateCreditChip(
          summarizeRunGateSpend(run, template, pendingGate, editState.edits),
          resolvedTier,
          gateAudience?.quickStudyCreditsPerRead,
        )
      : undefined

  /* The collapsed line's "N questions": summed off the STAGED bodies (a hand
     edit that changed the count wins), the same source the credit chip reads.
     A non-questions artifact (prose, a brief) contributes zero rather than
     failing the sum — `parseGateQuestions` returning null just means "not a
     questions payload", not "malformed". */
  const questionCount = pendingGate
    ? stagedBodies(artifacts, editState).reduce((total, body) => {
        const questions = parseGateQuestions(body)
        return total + (questions ? questions.length : 0)
      }, 0)
    : 0

  /* Everything `applyRevision` reads, refreshed after every render. A revision
     turn lands seconds after it was armed and must see the gate as it is NOW,
     not as it was when the reader hit send. */
  const live = useRef({
    canRevise,
    artifacts,
    editState,
    stepId: pendingGate?.stepId ?? null,
    onSubmitGate,
    onReviseStep,
    viewerOwnsRun: Boolean(run?.viewerOwnsRun),
  })
  useEffect(() => {
    live.current = {
      canRevise,
      artifacts,
      editState,
      stepId: pendingGate?.stepId ?? null,
      onSubmitGate,
      onReviseStep,
      viewerOwnsRun: Boolean(run?.viewerOwnsRun),
    }
  })

  useEffect(() => {
    setNotice(null)
    // A fresh dock (a new gate step, not a re-render of this one) always
    // mounts expanded, regardless of what `collapseSignal` last did.
    setIsCollapsed(false)
  }, [pendingGate?.stepId])

  /**
   * A revision turn has landed. THE WRITER REWRITES, THE GATE DOES NOT (D1).
   *
   * The intake turn only decided that the reader asked for a change; the words
   * they typed go to `POST /workflow-runs/:token/revise`, which redoes the
   * step that wrote these cards under its own skill, its org overlays and its
   * output checker. Splicing a classifier's prose into a typed question is
   * what produced an open end carrying baked-in choices, and it is gone.
   *
   * The cards the reader has RIGHT NOW ride along as the override, so a title
   * edited by hand a second before the message is what the rewrite starts
   * from (D2) and what stands if the rewrite fails.
   */
  const applyRevisionFor = useCallback(
    (
      stepId: string,
      tail: GateRevisionTail | null | undefined,
      instruction: string,
    ): GateRevisionOutcome => {
      const current = live.current

      /* Approved elsewhere, cancelled, or the run moved on to the next gate.
         Nothing was ever sent to the server, so there is nothing to undo and
         nothing to reconcile: the request is simply not made. */
      if (!current.canRevise || current.stepId !== stepId) {
        toast(i18n.t('threads.detail.toast.gateAlreadyClosed', { ns: 'app' }))
        return 'gate_moved'
      }

      /* Refused ("I cannot tell what to change"), some other phase, or a tail
         that did not survive its own contract. The prose that streamed into
         the thread is the explanation; the cards do not move and nothing is
         spent on a rewrite nobody could specify. */
      const asked = instruction.trim()
      if (
        !tail ||
        tail.status !== 'revision' ||
        tail.refused === true ||
        asked === '' ||
        !current.onReviseStep
      ) {
        return 'ignored'
      }

      setNotice(i18n.t('threads.detail.gate.rewriting', { ns: 'app' }))
      void current
        .onReviseStep({
          stepId,
          instruction: asked,
          edits: current.artifacts
            .filter(
              (artifact) => current.editState.edits[artifact.key] !== undefined,
            )
            .map((artifact) => ({
              stepKey: artifact.stepKey,
              slug: artifact.slug,
              index: artifact.index,
              body: current.editState.edits[artifact.key],
            })),
        })
        .then((accepted) => {
          if (accepted) {
            /* The adopted run takes the step out of `awaiting_approval`, so
               this dock unmounts and the busy line belongs to the run pane
               from here. The notice is cleared for the mount that comes back
               when the rewrite lands. */
            setNotice(null)
            return
          }
          setNotice(null)
          toast(i18n.t('threads.detail.toast.reviseFailed', { ns: 'app' }))
        })
      return 'revising'
    },
    [],
  )

  const revisionHandle = useMemo<GateRevisionHandle | null>(() => {
    if (!canRevise || !pendingGate) {
      return null
    }

    const stepId = pendingGate.stepId
    return {
      stepId,
      readStagedQuestions: () =>
        planGateRevision(
          stagedBodies(live.current.artifacts, live.current.editState),
        ).map((slot) => slot.text),
      applyRevision: (tail, instruction) =>
        applyRevisionFor(stepId, tail, instruction),
      approve: () => {
        const current = live.current
        if (
          !current.canRevise ||
          current.stepId !== stepId ||
          !current.viewerOwnsRun
        ) {
          return false
        }
        current.onSubmitGate({
          stepId,
          decision: 'approve',
          edits: current.artifacts
            .filter((artifact) => current.editState.edits[artifact.key] !== undefined)
            .map((artifact) => ({
              stepKey: artifact.stepKey,
              slug: artifact.slug,
              index: artifact.index,
              body: current.editState.edits[artifact.key],
            })),
        })
        return true
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity tracks the gate, not the render
  }, [canRevise, pendingGate?.stepId, applyRevisionFor])

  // Held in a ref so an inline arrow from the page cannot make publishing churn
  // once per render, which a page storing the handle in state would loop on.
  const publish = useRef(onRevisionHandleChange)
  useEffect(() => {
    publish.current = onRevisionHandleChange
  })

  useEffect(() => {
    publish.current?.(revisionHandle)
  }, [revisionHandle])

  useEffect(() => {
    return () => {
      publish.current?.(null)
    }
  }, [])

  if (!run || !pendingGate) {
    return null
  }

  const docksOnComposer = pendingGateDocksOnComposer(run, template)

  // The step the approve button sets in motion, named the way the run names
  // it (thread language on a live run), so the button reads "Write the
  // morning brief" rather than Continue.
  const nextStep =
    run.steps.find((step) => step.stepIndex === pendingGate.stepIndex + 1) ?? null
  const nextStepTitle = nextStep
    ? localizedWorkflowStepTitle({
        templateSlug: run.templateSlug,
        stepKey: nextStep.stepKey,
        language,
        fallback: nextStep.title,
      })
    : null

  const gateCard = (
    <RunGateCard
      step={pendingGate}
      artifacts={artifacts}
      actionable
      canEdit={isEditable}
      viewerOwnsRun={run.viewerOwnsRun}
      spendLine={buildRunSpendLine(run, template, pendingGate, editState.edits)}
      editState={editState}
      creditChip={creditChip}
      stimulusRefs={stimulusRefs}
      maxQuestions={maxQuestions}
      language={language}
      chrome={docksOnComposer ? 'card' : 'inline'}
      nextStepTitle={nextStepTitle}
      onSubmit={onSubmitGate}
      onCancelRun={onCancelRun}
    />
  )

  const stepTitle = localizedWorkflowStepTitle({
    templateSlug: run.templateSlug,
    stepKey: pendingGate.stepKey,
    language,
    fallback: pendingGate.title,
  })

  /* D6: collapsed is composer-placement only, and it is a dead end, not a
     smaller version of the card — no Approve, no Cancel, no preview. Just
     enough to say what is waiting and let the reviewer bring it back. */
  if (docksOnComposer && isCollapsed) {
    return (
      <div
        ref={dockRef}
        className={cn(className)}
        data-testid="thread-run-gate"
        data-placement="composer"
      >
        <div
          className={cn(
            /* The composer's above-slot pads the top only (pt-3); the bar pads
               the bottom to match so the line sits centred in its band.
               No wrapping: one line is the contract in both modes, so the
               title truncates instead of pushing the count and chip down. */
            'flex items-center gap-2 px-1 pb-4 pt-1',
            'animate-in fade-in slide-in-from-bottom-1 duration-200',
          )}
          data-testid="thread-run-gate-collapsed"
        >
          <Text
            as="span"
            size="xs"
            color="muted"
            className="shrink-0 leading-none"
            data-testid="thread-run-gate-waiting"
          >
            {t('threads.detail.gate.waitingOnYou')}
          </Text>
          <span aria-hidden="true" className="shrink-0 text-xs text-muted-foreground">
            &middot;
          </span>
          {/* Same size as the lead: the title earns its prominence from
              weight and full-strength foreground, not from a second type
              step, which on one row only made the line taller. */}
          <Text
            as="span"
            size="xs"
            weight="medium"
            className="min-w-0 flex-1 truncate leading-none"
            data-testid="thread-run-gate-title"
          >
            {stepTitle}
          </Text>
          {questionCount > 0 ? (
            <Text
              as="span"
              size="xs"
              color="muted"
              className="shrink-0 leading-none"
              data-testid="thread-run-gate-question-count"
            >
              {t('threads.detail.gate.questionCount', { count: questionCount })}
            </Text>
          ) : null}
          {creditChip ? (
            <div className="shrink-0" data-testid="thread-run-gate-collapsed-credit">
              {creditChip}
            </div>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 shrink-0 p-0"
            onClick={() => toggleCollapsed(false)}
            aria-label={t('threads.detail.gate.expand')}
            title={t('threads.detail.gate.expand')}
            aria-expanded={false}
            data-testid="thread-run-gate-expand"
          >
            <ChevronDown
              className="h-4 w-4 rotate-180 transition-transform duration-200"
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={dockRef}
      className={cn(
        docksOnComposer
          ? 'animate-in fade-in slide-in-from-bottom-1 duration-200'
          : 'mt-2',
        className,
      )}
      data-testid="thread-run-gate"
      data-placement={docksOnComposer ? 'composer' : 'thread'}
    >
      {docksOnComposer ? (
        /* One line, same shape as the collapsed bar: the step title is an
           arbitrary compiled heading, so the constant lead and the variable
           title stay separate nodes joined by a middot rather than folded
           into one translated sentence. `leading-none` is what actually buys
           the height back: the design-system `Text` bakes in `leading-7`, so
           a bare `xs` fragment would still reserve 28px on its own. */
        <div
          className="flex items-center gap-2 px-1"
          data-testid="thread-run-gate-header"
        >
          <Text
            as="span"
            size="xs"
            color="muted"
            className="shrink-0 leading-none"
            data-testid="thread-run-gate-waiting"
          >
            {t('threads.detail.gate.waitingOnYou')}
          </Text>
          <span aria-hidden="true" className="shrink-0 text-xs text-muted-foreground">
            &middot;
          </span>
          {/* Same size as the lead: the title earns its prominence from
              weight and full-strength foreground, not from a second type
              step, which on one row only made the line taller. */}
          <Text
            as="span"
            size="xs"
            weight="medium"
            className="min-w-0 flex-1 truncate leading-none"
            data-testid="thread-run-gate-title"
          >
            {stepTitle}
          </Text>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 shrink-0 p-0"
            onClick={() => toggleCollapsed(true)}
            aria-label={t('threads.detail.gate.collapse')}
            title={t('threads.detail.gate.collapse')}
            aria-expanded
            data-testid="thread-run-gate-collapse"
          >
            <ChevronDown
              className="h-4 w-4 transition-transform duration-200"
              aria-hidden="true"
            />
          </Button>
        </div>
      ) : null}

      {/* Said only when a rewrite left something alone. A half-typed draft is
          never clobbered, and the reviewer is told which card kept theirs. */}
      {notice ? (
        <Text
          size="xs"
          color="muted"
          className="px-1 pt-1"
          data-testid="thread-run-gate-notice"
        >
          {notice}
        </Text>
      ) : null}

      {docksOnComposer ? (
        /* Preview scrolls; spend + credit chip + Cancel/Approve stay sticky
           inside the card. Chip is optional: only when audience tier is known.
           Lowered from min(60vh,36rem) so a collapsible dock never opens as
           tall as the thread it sits on top of (D6). */
        <div
          className="max-h-[min(45vh,28rem)] overflow-y-auto"
          data-testid="thread-run-gate-preview"
        >
          {gateCard}
        </div>
      ) : (
        gateCard
      )}
    </div>
  )
}
